import { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import {
  Upload,
  FileText,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  Trash2,
  Send,
  Info,
  Save,
  Timer,
  HelpCircle,
  Eye,
  Shield,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { DocumentTemplateModal } from '../../components/banking/DocumentTemplateModal';
import { DocumentViewer } from '../../components/admin/DocumentViewer';
import {
  useDocumentTypes,
  useMyDocuments,
  useUploadDocument,
  useSubmitDocuments,
} from '../../hooks/useInviteLinks';

interface DocumentType {
  id: string;
  code: string;
  name: string;
  description?: string;
  kycType: string;
  entityType: string;
  isRequired: boolean;
  allowedMimeTypes: string[];
  maxFileSizeMb: number;
  displayOrder: number;
}

interface UploadedDocument {
  id: string;
  documentTypeCode: string;
  documentTypeName: string;
  fileName: string;
  originalName: string;
  mimeType: string;
  fileSize: number;
  status: string;
  rejectionReason?: string;
  uploadedAt: string;
}

interface KycSubmissionData {
  id: string;
  kycType: string;
  status: string;
  submittedAt?: string;
  rejectionReason?: string;
  additionalDocsReason?: string;
  resubmissionCount?: number;
  documents: UploadedDocument[];
  // SLA fields
  slaDeadline?: string;
  slaRemainingHours?: number;
  estimatedCompletionDate?: string;
}

interface RequiredDocuments {
  origin: DocumentType[];
  destination: DocumentType[];
}

interface KycStatusData {
  origin?: KycSubmissionData;
  destination?: KycSubmissionData;
  canOperate: { receive: boolean; send: boolean };
  pendingActions: string[];
  // SLA info
  slaConfig?: {
    originSlaHours: number;
    destinationSlaHours: number;
  };
}

type KycType = 'ORIGIN' | 'DESTINATION';

export function KycSubmission() {
  const [activeTab, setActiveTab] = useState<KycType>('ORIGIN');
  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [selectedDocTypeForTemplate, setSelectedDocTypeForTemplate] = useState<{ code: string; name: string } | null>(null);
  const [viewingDocument, setViewingDocument] = useState<{ url: string; name: string; mimeType: string } | null>(null);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const virtualAccountId = user?.virtualAccountId;
  const isPendingKyc = user?.status === 'PENDING_KYC';

  // If user is PENDING_KYC (invited via link), show invite-links upload flow
  if (isPendingKyc && !virtualAccountId) {
    return <InviteKycSubmission />;
  }

  // If user doesn't have a virtual account, show message
  if (!virtualAccountId) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Verificação KYC</h1>
          <p className="text-surface-400 mt-1">
            Envie seus documentos para verificação e libere todas as funcionalidades
          </p>
        </div>
        <div className="glass rounded-xl p-8 text-center border border-yellow-500/30">
          <AlertTriangle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-surface-100 mb-2">Conta Virtual Necessária</h3>
          <p className="text-surface-400 mb-4">
            Você precisa ter uma conta virtual vinculada para submeter documentos de KYC.
            Entre em contato com o administrador do seu tenant.
          </p>
          <button
            onClick={() => navigate('/banking')}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Get current submission data early for use in effects
  const getCurrentSubmissionData = () => {
    const currentSubmission = activeTab === 'ORIGIN' ? kycStatus?.origin : kycStatus?.destination;
    return {
      uploadedDocs: currentSubmission?.documents || [],
      currentStatus: currentSubmission?.status || 'DRAFT',
    };
  };

  // Buscar documentos requeridos
  const { data: requiredDocs, isLoading: loadingDocs } = useQuery<RequiredDocuments>({
    queryKey: ['kyc-required-documents'],
    queryFn: async () => {
      const response = await api.get('/kyc/required-documents');
      return response.data;
    },
  });

  // Buscar status atual do KYC
  const { data: kycStatus, isLoading: loadingStatus } = useQuery<KycStatusData>({
    queryKey: ['kyc-status', virtualAccountId],
    queryFn: async () => {
      const response = await api.get(`/kyc/my-status/${virtualAccountId}`);
      return response.data;
    },
    enabled: !!virtualAccountId,
  });

  // Mutation para upload de documento
  const uploadMutation = useMutation({
    mutationFn: async ({ file, kycType, documentTypeCode }: { 
      file: File; 
      kycType: KycType; 
      documentTypeCode: string;
    }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('kycType', kycType);
      formData.append('documentTypeCode', documentTypeCode);
      formData.append('virtualAccountId', virtualAccountId!);
      
      const response = await api.post('/kyc/upload-document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-status', virtualAccountId] });
      setUploadingDoc(null);
    },
    onError: () => {
      setUploadingDoc(null);
    },
  });

  // Mutation para submeter KYC
  const submitMutation = useMutation({
    mutationFn: async (kycType: KycType) => {
      const response = await api.post('/kyc/submit', { kycType, virtualAccountId });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-status', virtualAccountId] });
    },
  });

  // Mutation para resubmeter KYC
  const resubmitMutation = useMutation({
    mutationFn: async (kycType: KycType) => {
      const response = await api.post('/kyc/resubmit', { kycType, virtualAccountId });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-status', virtualAccountId] });
    },
  });

  // Mutation para deletar documento
  const deleteMutation = useMutation({
    mutationFn: async (documentId: string) => {
      await api.delete(`/kyc/documents/${documentId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-status', virtualAccountId] });
    },
  });

  // Mutation para salvar rascunho
  const saveDraftMutation = useMutation({
    mutationFn: async (kycType: KycType) => {
      const response = await api.post('/kyc/save-draft', { kycType, virtualAccountId });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-status', virtualAccountId] });
      setLastSaved(new Date());
      setIsSaving(false);
    },
    onError: () => {
      setIsSaving(false);
    },
  });

  // Auto-save draft every 30 seconds when there are changes
  useEffect(() => {
    const { uploadedDocs: docs, currentStatus: status } = getCurrentSubmissionData();
    if (status === 'DRAFT' && docs.length > 0) {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      autoSaveTimerRef.current = setTimeout(() => {
        setIsSaving(true);
        saveDraftMutation.mutate(activeTab);
      }, 30000); // 30 seconds
    }
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [kycStatus, activeTab, saveDraftMutation]);

  // Manual save draft
  const handleSaveDraft = () => {
    setIsSaving(true);
    saveDraftMutation.mutate(activeTab);
  };

  const currentDocs = activeTab === 'ORIGIN' ? requiredDocs?.origin : requiredDocs?.destination;
  const currentSubmission = activeTab === 'ORIGIN' ? kycStatus?.origin : kycStatus?.destination;
  const uploadedDocs = currentSubmission?.documents || [];

  // Verificar se documento já foi enviado
  const getUploadedDoc = (docTypeCode: string) => {
    return uploadedDocs.find(d => d.documentTypeCode === docTypeCode);
  };

  // Verificar se pode submeter
  const canSubmit = () => {
    if (!currentDocs) return false;
    const requiredCodes = currentDocs.filter(d => d.isRequired).map(d => d.code);
    const uploadedCodes = uploadedDocs.map(d => d.documentTypeCode);
    return requiredCodes.every(code => uploadedCodes.includes(code));
  };

  // Calculate progress percentage
  const calculateProgress = () => {
    if (!currentDocs || currentDocs.length === 0) return 0;
    const requiredDocs = currentDocs.filter(d => d.isRequired);
    if (requiredDocs.length === 0) return uploadedDocs.length > 0 ? 100 : 0;
    const uploadedRequired = requiredDocs.filter(d => 
      uploadedDocs.some(ud => ud.documentTypeCode === d.code)
    ).length;
    return Math.round((uploadedRequired / requiredDocs.length) * 100);
  };

  const progress = calculateProgress();

  // Status do KYC atual
  const currentStatus = currentSubmission?.status || 'DRAFT';
  const canEdit = ['DRAFT', 'REJECTED', 'ADDITIONAL_DOCS_REQUIRED'].includes(currentStatus);

  const handleUpload = useCallback((file: File, documentTypeCode: string) => {
    setUploadingDoc(documentTypeCode);
    uploadMutation.mutate({ file, kycType: activeTab, documentTypeCode });
  }, [activeTab, uploadMutation]);

  // Função para abrir o modal de template/exemplo
  const handleShowTemplate = useCallback((docTypeCode: string, docTypeName: string) => {
    setSelectedDocTypeForTemplate({ code: docTypeCode, name: docTypeName });
    setTemplateModalOpen(true);
  }, []);

  // Função para visualizar documento enviado
  const handleViewDocument = useCallback(async (docId: string, docName: string, mimeType: string) => {
    try {
      const response = await api.get(`/kyc/my-documents/${virtualAccountId}/${docId}/file`, {
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(response.data);
      setViewingDocument({ url: blobUrl, name: docName, mimeType });
    } catch (error) {
      console.error('Erro ao buscar documento:', error);
    }
  }, [virtualAccountId]);

  if (loadingDocs || loadingStatus) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-surface-100">Verificação KYC</h1>
        <p className="text-surface-400 mt-1">
          Envie seus documentos para verificação e libere todas as funcionalidades
        </p>
      </div>

      {/* Info Banner */}
      <div className="glass rounded-xl p-4 border border-blue-500/30">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-surface-100">Por que preciso verificar?</h3>
            <p className="text-sm text-surface-400 mt-1">
              A verificação KYC é obrigatória por regulamentação do Banco Central. 
              Envie seus documentos para que o administrador possa aprovar sua conta e liberar todas as operações.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs - only show if destination docs exist */}
      {requiredDocs?.destination && requiredDocs.destination.length > 0 && (
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('ORIGIN')}
            className={cn(
              "px-4 py-2 rounded-lg font-medium transition-colors",
              activeTab === 'ORIGIN'
                ? "bg-primary-500 text-white"
                : "bg-surface-800 text-surface-400 hover:text-surface-100"
            )}
          >
            Minha Verificação KYC
            {kycStatus?.origin?.status === 'APPROVED' && (
              <CheckCircle2 className="w-4 h-4 inline ml-2 text-green-400" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('DESTINATION')}
            className={cn(
              "px-4 py-2 rounded-lg font-medium transition-colors",
              activeTab === 'DESTINATION'
                ? "bg-primary-500 text-white"
                : "bg-surface-800 text-surface-400 hover:text-surface-100"
            )}
          >
            KYC de Destino
            <span className="text-xs ml-1 opacity-60">(opcional)</span>
            {kycStatus?.destination?.status === 'APPROVED' && (
              <CheckCircle2 className="w-4 h-4 inline ml-2 text-green-400" />
            )}
          </button>
        </div>
      )}

      {/* Progress Bar */}
      {canEdit && currentDocs && currentDocs.length > 0 && (
        <div className="glass rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-surface-300">Progresso do Envio</span>
            <div className="flex items-center gap-3">
              {/* Auto-save indicator */}
              {lastSaved && (
                <span className="text-xs text-surface-500 flex items-center gap-1">
                  <Save className="w-3 h-3" />
                  Salvo às {lastSaved.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              {isSaving && (
                <span className="text-xs text-primary-400 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Salvando...
                </span>
              )}
              <span className={cn(
                "text-sm font-bold",
                progress === 100 ? "text-green-400" : progress >= 50 ? "text-yellow-400" : "text-surface-400"
              )}>
                {progress}%
              </span>
            </div>
          </div>
          <div className="h-2 bg-surface-700 rounded-full overflow-hidden">
            <div 
              className={cn(
                "h-full transition-all duration-500 rounded-full",
                progress === 100 ? "bg-green-500" : progress >= 50 ? "bg-yellow-500" : "bg-primary-500"
              )}
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-surface-500">
              {uploadedDocs.length} de {currentDocs.filter(d => d.isRequired).length} documentos obrigatórios
            </span>
            {canEdit && uploadedDocs.length > 0 && (
              <button
                onClick={handleSaveDraft}
                disabled={isSaving}
                className="text-xs text-primary-400 hover:text-primary-300 flex items-center gap-1"
              >
                <Save className="w-3 h-3" />
                Salvar Rascunho
              </button>
            )}
          </div>
        </div>
      )}

      {/* SLA Info Banner */}
      {(currentStatus === 'SUBMITTED' || currentStatus === 'PENDING_REVIEW') && currentSubmission?.slaDeadline && (
        <div className="glass rounded-xl p-4 border border-blue-500/30 bg-blue-500/5">
          <div className="flex items-start gap-3">
            <Timer className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-surface-100">Prazo de Análise</h3>
              <p className="text-sm text-surface-400 mt-1">
                Previsão de conclusão: {new Date(currentSubmission.slaDeadline).toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
              {currentSubmission.slaRemainingHours !== undefined && (
                <p className="text-sm text-blue-400 mt-1">
                  {currentSubmission.slaRemainingHours > 0 
                    ? `Aproximadamente ${currentSubmission.slaRemainingHours} horas restantes`
                    : 'Análise em andamento'}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Status Banner - hide for ADDITIONAL_DOCS_REQUIRED since we show detailed banner below */}
      {currentStatus !== 'DRAFT' && currentStatus !== 'ADDITIONAL_DOCS_REQUIRED' && (
        <StatusBanner status={currentStatus} submission={currentSubmission} />
      )}

      {/* Previous Rejection Reason Banner (for resubmission) */}
      {(currentStatus === 'REJECTED' || currentStatus === 'ADDITIONAL_DOCS_REQUIRED') && (currentSubmission?.rejectionReason || currentSubmission?.additionalDocsReason) && (
        canSubmit() ? (
          <div className="glass rounded-xl p-4 border border-green-500/30 bg-green-500/5">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-green-400">Documentos prontos para reenvio</h3>
                <p className="text-sm text-surface-400 mt-1">
                  Todos os documentos obrigatórios foram enviados. Clique em "Reenviar para Análise" para submeter.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="glass rounded-xl p-4 border border-orange-500/30 bg-orange-500/5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-orange-400">
                  {currentStatus === 'ADDITIONAL_DOCS_REQUIRED' ? 'Documentos Adicionais Solicitados' : 'Motivo da Rejeição'}
                </h3>
                <p className="text-sm text-surface-400 mt-1">
                  {currentSubmission.additionalDocsReason || currentSubmission.rejectionReason}
                </p>
                <p className="text-xs text-surface-500 mt-2">
                  {currentStatus === 'ADDITIONAL_DOCS_REQUIRED' 
                    ? 'Envie os documentos solicitados abaixo e reenvie para análise.'
                    : 'Corrija os problemas apontados e reenvie seus documentos.'}
                  {currentSubmission.resubmissionCount !== undefined && (
                    <span className="ml-1">
                      ({3 - (currentSubmission.resubmissionCount || 0)} reenvios restantes)
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )
      )}

      {/* Lista de Documentos */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-surface-100">
          Documentos para Verificação
        </h2>

        {currentDocs?.map((docType) => {
          const uploaded = getUploadedDoc(docType.code);
          return (
            <DocumentUploadCard
              key={docType.id}
              docType={docType}
              uploadedDoc={uploaded}
              isUploading={uploadingDoc === docType.code}
              canEdit={canEdit}
              onUpload={(file) => handleUpload(file, docType.code)}
              onDelete={() => {
                if (uploaded) deleteMutation.mutate(uploaded.id);
              }}
              onShowTemplate={() => handleShowTemplate(docType.code, docType.name)}
              onView={uploaded ? () => handleViewDocument(uploaded.id, uploaded.originalName, uploaded.mimeType) : undefined}
            />
          );
        })}
      </div>

      {/* Botão de Submeter */}
      {canEdit && (
        <div className="flex justify-end gap-3">
          {currentStatus === 'REJECTED' || currentStatus === 'ADDITIONAL_DOCS_REQUIRED' ? (
            <button
              onClick={() => resubmitMutation.mutate(activeTab)}
              disabled={!canSubmit() || resubmitMutation.isPending}
              className={cn(
                "px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors",
                canSubmit()
                  ? "bg-primary-500 text-white hover:bg-primary-600"
                  : "bg-surface-700 text-surface-500 cursor-not-allowed"
              )}
            >
              {resubmitMutation.isPending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
              Reenviar para Análise
            </button>
          ) : (
            <button
              onClick={() => submitMutation.mutate(activeTab)}
              disabled={!canSubmit() || submitMutation.isPending}
              className={cn(
                "px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors",
                canSubmit()
                  ? "bg-primary-500 text-white hover:bg-primary-600"
                  : "bg-surface-700 text-surface-500 cursor-not-allowed"
              )}
            >
              {submitMutation.isPending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
              Enviar para Análise
            </button>
          )}
        </div>
      )}

      {/* Modal de Template/Exemplo de Documento */}
      <DocumentTemplateModal
        isOpen={templateModalOpen}
        onClose={() => {
          setTemplateModalOpen(false);
          setSelectedDocTypeForTemplate(null);
        }}
        documentTypeCode={selectedDocTypeForTemplate?.code || ''}
        documentTypeName={selectedDocTypeForTemplate?.name}
      />

      {/* Modal de Visualização de Documento */}
      {viewingDocument && (
        <DocumentViewer
          documentUrl={viewingDocument.url}
          documentName={viewingDocument.name}
          mimeType={viewingDocument.mimeType}
          onClose={() => {
            URL.revokeObjectURL(viewingDocument.url);
            setViewingDocument(null);
          }}
          onError={(error) => console.error('Erro ao visualizar documento:', error)}
        />
      )}
    </div>
  );
}

// Componente de Status Banner
function StatusBanner({ status, submission }: { status: string; submission?: KycSubmissionData }) {
  const config: Record<string, { bg: string; border: string; icon: React.ElementType; title: string; color: string }> = {
    SUBMITTED: {
      bg: 'bg-blue-500/10',
      border: 'border-blue-500/30',
      icon: Loader2,
      title: 'Documentos Enviados',
      color: 'text-blue-400',
    },
    PENDING_REVIEW: {
      bg: 'bg-yellow-500/10',
      border: 'border-yellow-500/30',
      icon: Loader2,
      title: 'Em Análise',
      color: 'text-yellow-400',
    },
    APPROVED: {
      bg: 'bg-green-500/10',
      border: 'border-green-500/30',
      icon: CheckCircle2,
      title: 'Aprovado',
      color: 'text-green-400',
    },
    REJECTED: {
      bg: 'bg-red-500/10',
      border: 'border-red-500/30',
      icon: XCircle,
      title: 'Rejeitado',
      color: 'text-red-400',
    },
    ADDITIONAL_DOCS_REQUIRED: {
      bg: 'bg-orange-500/10',
      border: 'border-orange-500/30',
      icon: AlertTriangle,
      title: 'Documentos Adicionais Necessários',
      color: 'text-orange-400',
    },
  };

  const cfg = config[status];
  if (!cfg) return null;

  const Icon = cfg.icon;

  return (
    <div className={cn("rounded-xl p-4 border", cfg.bg, cfg.border)}>
      <div className="flex items-start gap-3">
        <Icon className={cn("w-5 h-5 flex-shrink-0 mt-0.5", cfg.color, status === 'PENDING_REVIEW' && 'animate-spin')} />
        <div>
          <h3 className={cn("font-medium", cfg.color)}>{cfg.title}</h3>
          {status === 'REJECTED' && submission?.rejectionReason && (
            <p className="text-sm text-surface-400 mt-1">
              Motivo: {submission.rejectionReason}
            </p>
          )}
          {status === 'ADDITIONAL_DOCS_REQUIRED' && submission?.additionalDocsReason && (
            <p className="text-sm text-surface-400 mt-1">
              {submission.additionalDocsReason}
            </p>
          )}
          {(status === 'SUBMITTED' || status === 'PENDING_REVIEW') && (
            <p className="text-sm text-surface-400 mt-1">
              Seus documentos estão sendo analisados. Prazo estimado: 48 horas.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// Tipos de arquivo permitidos para segurança
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.pdf'];
const MAX_FILE_SIZE_MB = 10;

// Função para validar arquivo antes do upload
function validateFileForUpload(file: File): { isValid: boolean; error?: string } {
  // Validar extensão
  const fileName = file.name.toLowerCase();
  const hasValidExtension = ALLOWED_EXTENSIONS.some(ext => fileName.endsWith(ext));
  if (!hasValidExtension) {
    return { 
      isValid: false, 
      error: `Formato não permitido. Use apenas: ${ALLOWED_EXTENSIONS.join(', ').toUpperCase()}` 
    };
  }

  // Validar MIME type
  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    return { 
      isValid: false, 
      error: `Tipo de arquivo inválido. Permitidos: PNG, JPEG, PDF` 
    };
  }

  // Validar tamanho
  const fileSizeMB = file.size / (1024 * 1024);
  if (fileSizeMB > MAX_FILE_SIZE_MB) {
    return { 
      isValid: false, 
      error: `Arquivo muito grande (${fileSizeMB.toFixed(1)}MB). Máximo: ${MAX_FILE_SIZE_MB}MB` 
    };
  }

  return { isValid: true };
}

// Componente de Card de Upload
function DocumentUploadCard({
  docType,
  uploadedDoc,
  isUploading,
  canEdit,
  onUpload,
  onDelete,
  onShowTemplate,
  onView,
}: {
  docType: DocumentType;
  uploadedDoc?: UploadedDocument;
  isUploading: boolean;
  canEdit: boolean;
  onUpload: (file: File) => void;
  onDelete: () => void;
  onShowTemplate: () => void;
  onView?: () => void;
}) {
  const [uploadError, setUploadError] = useState<string | null>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setUploadError(null);
    
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      
      // Validar arquivo antes do upload
      const validation = validateFileForUpload(file);
      if (!validation.isValid) {
        setUploadError(validation.error || 'Arquivo inválido');
        return;
      }
      
      onUpload(file);
    }
  }, [onUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: docType.allowedMimeTypes.reduce((acc, type) => {
      acc[type] = [];
      return acc;
    }, {} as Record<string, string[]>),
    maxSize: docType.maxFileSizeMb * 1024 * 1024,
    multiple: false,
    disabled: !canEdit || isUploading,
  });

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="glass rounded-xl p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="font-medium text-surface-100 flex items-center gap-2">
            {docType.name}
            {docType.isRequired && (
              <span className="text-xs text-red-400">*Obrigatório</span>
            )}
          </h3>
          {docType.description && (
            <p className="text-sm text-surface-400 mt-1">{docType.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Botão Ver Exemplo */}
          <button
            onClick={onShowTemplate}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-400 bg-primary-500/10 hover:bg-primary-500/20 border border-primary-500/30 rounded-lg transition-colors"
            title="Ver exemplo de como enviar este documento"
          >
            <HelpCircle className="w-3.5 h-3.5" />
            Ver exemplo
          </button>
          {uploadedDoc && (
            <>
              {uploadedDoc.status === 'VERIFIED' && (
                <CheckCircle2 className="w-5 h-5 text-green-400" />
              )}
              {uploadedDoc.status === 'REJECTED' && (
                <XCircle className="w-5 h-5 text-red-400" />
              )}
              {uploadedDoc.status === 'UPLOADED' && (
                <Loader2 className="w-5 h-5 text-yellow-400" />
              )}
            </>
          )}
        </div>
      </div>

      {/* Error message display */}
      {uploadError && (
        <div className="mb-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-400">{uploadError}</p>
            <button
              onClick={() => setUploadError(null)}
              className="text-xs text-red-300 hover:text-red-200 mt-1 underline"
            >
              Fechar
            </button>
          </div>
        </div>
      )}

      {uploadedDoc ? (
        <div className="flex items-center justify-between p-3 bg-surface-800 rounded-lg">
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8 text-primary-400" />
            <div>
              <p className="text-sm font-medium text-surface-100">{uploadedDoc.originalName}</p>
              <p className="text-xs text-surface-500">
                {formatFileSize(uploadedDoc.fileSize)} • Enviado em {new Date(uploadedDoc.uploadedAt).toLocaleDateString('pt-BR')}
              </p>
              {uploadedDoc.status === 'REJECTED' && uploadedDoc.rejectionReason && (
                <p className="text-xs text-red-400 mt-1">{uploadedDoc.rejectionReason}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {onView && (
              <button
                onClick={onView}
                className="p-2 text-surface-400 hover:text-primary-400 transition-colors"
                title="Visualizar documento"
              >
                <Eye className="w-4 h-4" />
              </button>
            )}
            {canEdit && (
              <button
                onClick={onDelete}
                className="p-2 text-surface-400 hover:text-red-400 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={cn(
            "border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer",
            isDragActive
              ? "border-primary-500 bg-primary-500/10"
              : "border-surface-700 hover:border-surface-600",
            !canEdit && "opacity-50 cursor-not-allowed"
          )}
        >
          <input {...getInputProps()} />
          {isUploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
              <p className="text-sm text-surface-400">Enviando...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload className="w-8 h-8 text-surface-500" />
              <p className="text-sm text-surface-400">
                {isDragActive
                  ? "Solte o arquivo aqui"
                  : "Arraste um arquivo ou clique para selecionar"}
              </p>
              <p className="text-xs text-surface-500">
                Formatos: {docType.allowedMimeTypes.map(t => t.split('/')[1].toUpperCase()).join(', ')} • 
                Máx: {docType.maxFileSizeMb}MB
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default KycSubmission;

// Component for PENDING_KYC users (invited via link) - uses invite-links endpoints
function InviteKycSubmission() {
  const { user } = useAuthStore();
  const accountType = (user as any)?.accountType || 'PF';
  const { data: docTypes, isLoading: loadingTypes } = useDocumentTypes(user?.tenantId, accountType);
  const { data: myDocs, isLoading: loadingDocs } = useMyDocuments();
  const uploadMutation = useUploadDocument();
  const submitMutation = useSubmitDocuments();
  const [uploadingType, setUploadingType] = useState<string | null>(null);

  const getUploadedDoc = (code: string) => myDocs?.find((d) => d.documentType === code);

  const requiredTypes = docTypes?.filter((dt) => dt.isRequired) || [];
  const uploadedRequired = requiredTypes.filter((dt) => getUploadedDoc(dt.code));
  const allSubmitted = myDocs && myDocs.length > 0 && myDocs.every((d) => d.status === 'PENDING' || d.status === 'UPLOADED' || d.status === 'APPROVED');
  const canSubmit = requiredTypes.length > 0 && uploadedRequired.length === requiredTypes.length;
  const progress = requiredTypes.length > 0 ? Math.round((uploadedRequired.length / requiredTypes.length) * 100) : 0;

  const handleUpload = useCallback(
    async (file: File, documentType: string) => {
      setUploadingType(documentType);
      try {
        await uploadMutation.mutateAsync({ file, documentType });
      } finally {
        setUploadingType(null);
      }
    },
    [uploadMutation]
  );

  const handleSubmit = async () => {
    try {
      await submitMutation.mutateAsync();
    } catch {
      // Error handled by mutation
    }
  };

  if (loadingTypes || loadingDocs) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-surface-100">Verificação KYC</h1>
        <p className="text-surface-400 mt-1">
          Envie seus documentos para verificação e ativação da sua conta
        </p>
      </div>

      {/* Info Banner */}
      <div className="glass rounded-xl p-4 border border-blue-500/30">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-surface-100">Verificação obrigatória</h3>
            <p className="text-sm text-surface-400 mt-1">
              Para ativar sua conta, envie os documentos abaixo. O administrador irá revisar e aprovar sua verificação.
            </p>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      {docTypes && docTypes.length > 0 && (
        <div className="glass rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-surface-300">Progresso do Envio</span>
            <span
              className={cn(
                'text-sm font-bold',
                progress === 100 ? 'text-green-400' : progress >= 50 ? 'text-yellow-400' : 'text-surface-400'
              )}
            >
              {progress}%
            </span>
          </div>
          <div className="h-2 bg-surface-700 rounded-full overflow-hidden">
            <div
              className={cn(
                'h-full transition-all duration-500 rounded-full',
                progress === 100 ? 'bg-green-500' : progress >= 50 ? 'bg-yellow-500' : 'bg-primary-500'
              )}
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="text-xs text-surface-500 mt-2 block">
            {uploadedRequired.length} de {requiredTypes.length} documentos obrigatórios
          </span>
        </div>
      )}

      {/* Already submitted banner */}
      {allSubmitted && myDocs && myDocs.length > 0 && (
        <div className="glass rounded-xl p-4 border border-blue-500/30 bg-blue-500/5">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-surface-100">Documentos em análise</h3>
              <p className="text-sm text-surface-400 mt-1">
                Seus documentos já foram enviados e estão sendo analisados pelo administrador.
                Você pode substituir documentos abaixo se necessário.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Document Upload Cards */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-surface-100">Documentos para Verificação</h2>
        {docTypes?.map((dt) => (
          <InviteDocUploadCard
            key={dt.code}
            docType={dt}
            uploadedDoc={getUploadedDoc(dt.code)}
            isUploading={uploadingType === dt.code}
            onUpload={(file) => handleUpload(file, dt.code)}
          />
        ))}
      </div>

      {/* Error */}
      {(uploadMutation.error || submitMutation.error) && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">
            {(uploadMutation.error as any)?.response?.data?.message ||
              (submitMutation.error as any)?.response?.data?.message ||
              'Erro ao processar. Tente novamente.'}
          </p>
        </div>
      )}

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSubmit}
          disabled={!canSubmit || submitMutation.isPending}
          className={cn(
            'px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors',
            canSubmit
              ? 'bg-primary-500 text-white hover:bg-primary-600'
              : 'bg-surface-700 text-surface-500 cursor-not-allowed'
          )}
        >
          {submitMutation.isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          Enviar para Análise
        </button>
      </div>

      {!canSubmit && requiredTypes.length > 0 && (
        <p className="text-xs text-surface-500 text-right">
          Envie todos os documentos obrigatórios para continuar
        </p>
      )}

      {/* Success message */}
      {submitMutation.isSuccess && (
        <div className="glass rounded-xl p-4 border border-green-500/30 bg-green-500/5">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-green-400">Documentos enviados com sucesso</h3>
              <p className="text-sm text-surface-400 mt-1">
                Seus documentos foram enviados para análise. O administrador irá revisar e ativar sua conta em breve.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Upload card for invite-links flow
function InviteDocUploadCard({
  docType,
  uploadedDoc,
  isUploading,
  onUpload,
}: {
  docType: { code: string; name: string; description?: string; isRequired: boolean; maxFileSizeMb: number; allowedMimeTypes: string[] };
  uploadedDoc?: { id: string; originalName: string; fileSize: number; status: string; rejectionReason?: string; createdAt: string };
  isUploading: boolean;
  onUpload: (file: File) => void;
}) {
  const [uploadError, setUploadError] = useState<string | null>(null);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setUploadError(null);
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const validation = validateFileForUpload(file);
        if (!validation.isValid) {
          setUploadError(validation.error || 'Arquivo inválido');
          return;
        }
        const sizeMb = file.size / (1024 * 1024);
        if (sizeMb > docType.maxFileSizeMb) {
          setUploadError(`Arquivo muito grande (${sizeMb.toFixed(1)}MB). Máximo: ${docType.maxFileSizeMb}MB`);
          return;
        }
        onUpload(file);
      }
    },
    [onUpload, docType.maxFileSizeMb]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: docType.allowedMimeTypes.reduce((acc: Record<string, string[]>, t: string) => {
      acc[t] = [];
      return acc;
    }, {}),
    maxSize: docType.maxFileSizeMb * 1024 * 1024,
    multiple: false,
    disabled: isUploading,
  });

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isRejected = uploadedDoc?.status === 'REJECTED';

  return (
    <div className="glass rounded-xl p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="font-medium text-surface-100 flex items-center gap-2">
            {docType.name}
            {docType.isRequired && <span className="text-xs text-red-400">*Obrigatório</span>}
          </h3>
          {docType.description && <p className="text-sm text-surface-400 mt-1">{docType.description}</p>}
        </div>
        {uploadedDoc && (
          <div className="flex items-center">
            {uploadedDoc.status === 'APPROVED' && <CheckCircle2 className="w-5 h-5 text-green-400" />}
            {uploadedDoc.status === 'REJECTED' && <XCircle className="w-5 h-5 text-red-400" />}
            {(uploadedDoc.status === 'UPLOADED' || uploadedDoc.status === 'PENDING') && (
              <Loader2 className="w-5 h-5 text-yellow-400" />
            )}
          </div>
        )}
      </div>

      {/* Rejection reason */}
      {isRejected && uploadedDoc?.rejectionReason && (
        <div className="mb-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-sm text-red-400">
            <strong>Rejeitado:</strong> {uploadedDoc.rejectionReason}
          </p>
          <p className="text-xs text-surface-500 mt-1">Envie um novo documento abaixo.</p>
        </div>
      )}

      {/* Upload error */}
      {uploadError && (
        <div className="mb-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-400">{uploadError}</p>
            <button onClick={() => setUploadError(null)} className="text-xs text-red-300 hover:text-red-200 mt-1 underline">
              Fechar
            </button>
          </div>
        </div>
      )}

      {uploadedDoc && !isRejected ? (
        <div className="flex items-center justify-between p-3 bg-surface-800 rounded-lg">
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8 text-primary-400" />
            <div>
              <p className="text-sm font-medium text-surface-100">{uploadedDoc.originalName}</p>
              <p className="text-xs text-surface-500">
                {formatFileSize(uploadedDoc.fileSize)} • Enviado em{' '}
                {new Date(uploadedDoc.createdAt).toLocaleDateString('pt-BR')}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={cn(
            'border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer',
            isDragActive ? 'border-primary-500 bg-primary-500/10' : 'border-surface-700 hover:border-surface-600'
          )}
        >
          <input {...getInputProps()} />
          {isUploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
              <p className="text-sm text-surface-400">Enviando...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload className="w-8 h-8 text-surface-500" />
              <p className="text-sm text-surface-400">
                {isDragActive ? 'Solte o arquivo aqui' : 'Arraste um arquivo ou clique para selecionar'}
              </p>
              <p className="text-xs text-surface-500">
                Formatos: {docType.allowedMimeTypes.map((t) => t.split('/')[1].toUpperCase()).join(', ')} • Máx:{' '}
                {docType.maxFileSizeMb}MB
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
